import { useCallback, useEffect, useRef, useState } from "react";
import type { CommentsSummaryResponse } from "@multi-chat/shared";
import { getCommentsSummary } from "../../../api/social-comments-api.js";
import { useInboxRealtime } from "../InboxRealtimeProvider.js";

const FOCUS_REFETCH_MS = 60_000;

/**
 * Comments open/unread counts + reconnect permission flags.
 * Refetches on SOCIAL_COMMENT_CHANGED, EventSource reconnect, and tab focus
 * (focus path is a V1 safety net if SSE delivery is missed).
 */
export function useCommentsSummary(organizationId: number): {
  summary: CommentsSummaryResponse | null;
  loading: boolean;
  refresh: () => void;
} {
  const { subscribeSocialCommentChanged, subscribeReconnect } = useInboxRealtime();
  const [summary, setSummary] = useState<CommentsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const orgRef = useRef(organizationId);
  const abortRef = useRef<AbortController | null>(null);
  const hasSummaryRef = useRef(false);
  const lastFocusFetchRef = useRef(0);

  const refresh = useCallback(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const orgId = organizationId;
    if (!hasSummaryRef.current) {
      setLoading(true);
    }
    void getCommentsSummary(orgId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        hasSummaryRef.current = true;
        setSummary(response);
        setLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted || orgRef.current !== orgId) return;
        setLoading(false);
      });
  }, [organizationId]);

  useEffect(() => {
    orgRef.current = organizationId;
    hasSummaryRef.current = false;
    setSummary(null);
    setLoading(true);
    refresh();
    return () => {
      abortRef.current?.abort();
    };
  }, [organizationId, refresh]);

  useEffect(() => {
    const unsubEvent = subscribeSocialCommentChanged(() => {
      refresh();
    });
    const unsubReconnect = subscribeReconnect(() => {
      refresh();
    });
    const onVisibility = (): void => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current < FOCUS_REFETCH_MS) return;
      lastFocusFetchRef.current = now;
      refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      unsubEvent();
      unsubReconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh, subscribeReconnect, subscribeSocialCommentChanged]);

  return { summary, loading, refresh };
}
